import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import { BooleanTags } from "../../components/common/registerUtils";
import { API_PARTNER_CHARGES, API_PARTNERS } from "../../utils/apis";
import {
  createDeleteAction,
  createEditAction,
  createViewAction,
  formatCurrency,
  getForm,
  getRegister,
  removeUnnecessaryFields,
} from "../../utils/helpers";
import { URL_PARTNER_CHARGES } from "../../utils/urls";
import useGet from "../common/useGet";
import { useParams } from "react-router-dom";

const url = URL_PARTNER_CHARGES;

// REGISTER
export const usePartnerCharges = () => {
  const { id } = useParams<{ id?: string }>();

  const tableColumns = () => [
    { id: "partner_name", header: "Partner Name", filter: {} },
    { id: "partner_code", header: "Partner Code", filter: {} },
    { id: "fee_type", header: "Fee Type", filter: {} },
    { id: "lower_limit", header: "Lower Limit", filter: {} },
    { id: "upper_limit", header: "Upper limit", filter: {} },
    { id: "fee", header: "Fee", filter: {} },
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      const itemId = item.id;

      const safeLogItem = { ...item };
      delete safeLogItem._owner;
      delete safeLogItem._store;
      delete safeLogItem._self;
      delete safeLogItem._source;

      const cleanedItem = removeUnnecessaryFields({ ...item }, [
        "is_yoya_charged",
        "is_customer_charged",
        "is_merchant_charged",
      ]);

      const result = {
        ...cleanedItem,
        lower_limit: formatCurrency(cleanedItem?.lower_limit),
        upper_limit: formatCurrency(cleanedItem?.upper_limit),
        fee: cleanedItem?.fee,
        id: itemId,
        actions: (
          <FormActions
            actions={[
              createEditAction(url, itemId),
              createDeleteAction(
                `${API_PARTNER_CHARGES}${itemId}/`,
                `${item?.partner_name || ""}`,
              ),
              createViewAction(url, itemId),
            ]}
          />
        ),
      };

      return result;
    });
  };

  const viewData = (item: any) => {
    return {
      sections: [
        {
          title: "Partner charges Details",
          fields: {
            partner_code: item?.partner_code,
            partner_name: item?.partner_name,
            fee: item?.fee,
            fee_type: item?.fee_type,
            lower_limit: item?.lower_limit,
            upper_limit: item?.upper_limit,
            is_percent: <BooleanTags status={item?.is_percent} />,
            is_active: <BooleanTags status={item?.is_active} />,
          },
        },
      ],
    };
  };

  const formRoute = getForm(url);

  const endpoint = `transact/partner-charges/${id}/logs/`;

  return { tableData, tableColumns, formRoute, viewData, endpoint };
};

// FORM
export const usePartnerChargesForm = ({ form }) => {
  const {
    loadData: getPartners,
    data: partners,
    loading: partnersLoading,
    error: partnersError,
  } = useGet();

  useEffect(() => {
    getPartners({ api: API_PARTNERS });
  }, []);

  const sections: any = [
    {
      title: "Partner Charges Details",
      fields: [
        {
          field_name: "partner",
          label: "Partner Name",
          type: "select",
          props: {
            required: true,
            options: partners?.results?.map((item) => ({
              value: item.partner_code,
              label: item.partner_name,
            })),
          },
          loading: partnersLoading,
        },
        {
          field_name: "fee_type",
          type: "select",
          label: "Fee type",
          props: { required: true },
          options_data: [
            { id: "fixed", name: "Fixed" },
            { id: "percentage", name: "Percentage" },
            { id: "tiered", name: "Tiered" },
          ],
        },
        {
          field_name: "is_yoya_charged",
          type: "select",
          label: "Is OnePayX charged",
          props: { required: false },
          options_data: [
            { id: true, name: "True" },
            { id: false, name: "False" },
          ],
        },
        {
          field_name: "is_customer_charged",
          type: "select",
          label: "Is customer charged",
          props: { required: false },
          options_data: [
            { id: true, name: "True" },
            { id: false, name: "False" },
          ],
        },
        {
          field_name: "is_merchant_charged",
          type: "select",
          label: "Is merchant charged",
          props: { required: false },
          options_data: [
            { id: true, name: "True" },
            { id: false, name: "False" },
          ],
        },
        {
          field_name: "fee",
          label: "Fee",
          type: "text",
          props: { required: true },
        },
        {
          field_name: "lower_limit",
          label: "Lower Limit",
          type: "number",
          props: { required: true },
          visible: form?.fee_type === "tiered",
        },
        {
          field_name: "upper_limit",
          label: "Upper Limit",
          type: "number",
          props: { required: true },
          visible: form?.fee_type === "tiered",
        },
        {
          field_name: "is_percent",
          type: "select",
          label: "Is Percentage",
          props: { required: true },
          visible: ["tiered", "percentage"]?.includes(form?.fee_type),
          options_data: [
            { id: true, name: "True" },
            { id: false, name: "False" },
          ],
        },
        {
          field_name: "is_active",
          type: "select",
          label: "Is Active",
          // props: { required: true },
          options_data: [
            { id: true, name: "True" },
            { id: false, name: "False" },
          ],
        },
      ],
    },
  ];

  const redirect = getRegister(url);

  return {
    sections,
    redirect,
    partnersLoading,
    partnersError,
    onSubmit: submitPartnerCharges,
  };
};

// SUBMIT
export function submitPartnerCharges(payload: any, id: string | number) {
  //   UPDATE
  if (id) {
    return {
      id,
      api: API_PARTNER_CHARGES,
      params: {
        ...payload,
      },
    };
  }
  //   CREATE
  return {
    api: API_PARTNER_CHARGES,
    params: {
      ...payload,
      partner_code: payload.partner,
    },
  };
}
